import React from 'react';

import { List, Modal, Tag } from 'antd-v5';
import _ from 'lodash';
import { MdEvent } from 'react-icons/md';
import { FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Heading, Text } from 'scouting-ui/lib';

import type {
  PostYouthMeritBadgesReq,
  UpdateBulkMeritBadgeRequirements,
} from '@appTypes/esb';
import { useMBDict } from '@dict/duck/hooks';
import { mbcApi } from '@modules/meritBadgeCounselors/api';
import {
  usePostBulkYouthMeritBadgeRequirementsMutation,
  usePostYouthMeritBadgesMutation,
} from '@modules/rtk/esb-api';
import { getErrorDesc } from '@modules/rtk/utils/esbApiUtils';
import tagFactory from '@modules/rtk/utils/tagFactory';
import { getPreferredName } from '@shared/utils/preferredName';
import { toastService } from '@toasts';
import { formatToAPIDate } from '@utils/datesLuxon';

import {
  mbcProfileMIDSel,
  modalSel,
  selectedMBsToApproveSel,
  selectedReqsToApproveSel,
} from '../duck/selectors';
import { mbcDashboardActions } from '../duck/slice';
import MBImage from './MBImage';

export default function ApproveConfirmationModal() {
  const selectedMeritBadges = useSelector(selectedMBsToApproveSel);
  const selectedReqs = useSelector(selectedReqsToApproveSel);
  const mbcMemberId = useSelector(mbcProfileMIDSel);
  const toApproveModalOpen = useSelector(modalSel('toApprove'));
  const { meritBadgesById } = useMBDict();
  const [postMBs, { isLoading: isPosting }] = usePostYouthMeritBadgesMutation();
  const [postRequirements, { isLoading: isPostingReqs }] =
    usePostBulkYouthMeritBadgeRequirementsMutation();
  const dispatch = useDispatch();
  const closeModal = () =>
    dispatch(mbcDashboardActions.setModal({ modal: 'toApprove', open: false }));

  async function updateMeritBadge() {
    if (!selectedMeritBadges.length) return Promise.resolve();

    const payloads = selectedMeritBadges.map(item => {
      const youthPayload: PostYouthMeritBadgesReq[number] = {
        override: false,
        userId: item.youth.userId,
        meritBadges: [
          {
            id: item.mb.meritBadgeId,
            counselorApprovedDate: formatToAPIDate(new Date()),
          },
        ],
      };
      return youthPayload;
    });
    return postMBs(payloads).unwrap();
  }

  async function updateRequirements() {
    if (_.isEmpty(selectedReqs)) return Promise.resolve();

    const promises = _.chain(selectedReqs)
      .groupBy(req => [req.mb.meritBadgeId].join(':'))
      .map(perMBValues => {
        const first = perMBValues[0];
        const meritBadgeId = first.mb.meritBadgeId;

        const payload = _.chain(perMBValues)
          .groupBy(item => [item.youth.userId, item.mb.versionId].join(':'))
          .map(userGroup => {
            const first = userGroup[0];
            const requirements = userGroup.map(item => item.requirement);

            const userPayload: UpdateBulkMeritBadgeRequirements = {
              versionId: first.mb.versionId,
              userId: first.youth.userId,
              requirements: requirements.map(req => ({
                id: req.id,
                counselorApprovedDate: formatToAPIDate(new Date()),
              })),
            };
            return userPayload;
          })
          .value();

        return {
          payload,
          meritBadgeId,
        };
      })
      .map(perMBRequest =>
        postRequirements({
          payload: perMBRequest.payload,
          meritBadgeId: perMBRequest.meritBadgeId,
        }).unwrap(),
      )
      .value();

    return Promise.all(promises);
  }

  return (
    <Modal
      width={800}
      open={toApproveModalOpen}
      onClose={closeModal}
      okButtonProps={{ loading: isPosting || isPostingReqs }}
      onCancel={closeModal}
      onOk={async () => {
        try {
          try {
            await updateMeritBadge();
          } catch (err) {
            toastService.error(getErrorDesc(err));
          }
          try {
            await updateRequirements();
            dispatch(
              mbcDashboardActions.setModal({ modal: 'toApprove', open: false }),
            );
          } catch (err) {
            toastService.error(getErrorDesc(err));
          }
        } finally {
          dispatch(
            mbcApi.util.invalidateTags([
              tagFactory.MBCDashboard.userDashboard(mbcMemberId!),
            ]),
          );
        }
      }}
      cancelButtonProps={{
        type: 'default',
        disabled: isPosting || isPostingReqs,
      }}
      title={<FormattedMessage id="shared.confirm" />}
    >
      <Text>
        <FormattedMessage id="mbcDashboard.approveModal.description" />
      </Text>

      <List
        dataSource={selectedMeritBadges}
        size="small"
        header={
          <Heading font="display-h5">
            <FormattedMessage id="mbcDashboard.approveModal.mbList.title" />
          </Heading>
        }
        renderItem={item => {
          const mbName = item.mb.name;
          const mbImage = meritBadgesById.get(
            item.mb.meritBadgeId,
          )?.imageUrl100;
          const youthName = getPreferredName(item.youth);
          const dateCompleted = item.mb.dateCompleted;
          return (
            <List.Item>
              <List.Item.Meta
                avatar={<MBImage width={32} height={32} src={mbImage} />}
                title={
                  <>
                    {youthName}{' '}
                    <Tag
                      icon={
                        <MdEvent
                          style={{
                            fontSize: 13,
                            verticalAlign: 'text-top',
                          }}
                        />
                      }
                    >
                      {' '}
                      Completed: {dateCompleted}
                    </Tag>
                  </>
                }
                description={<Text color="color-text-light">{mbName} </Text>}
              />
            </List.Item>
          );
        }}
      />
      <List
        dataSource={selectedReqs}
        size="small"
        header={
          <Heading font="display-h5">
            <FormattedMessage id="mbcDashboard.approveModal.reqsList.title" />
          </Heading>
        }
        renderItem={item => {
          const mbName = item.mb.name;
          const name = [
            mbName,
            '-',
            'Requirement ',
            item.requirement.number || item.requirement.listNumber,
          ].join(' ');
          const mbImage = meritBadgesById.get(
            item.mb.meritBadgeId,
          )?.imageUrl100;
          const youthName = getPreferredName(item.youth);
          const dateCompleted = item.requirement.dateCompleted;
          return (
            <List.Item>
              <List.Item.Meta
                avatar={<MBImage width={32} height={32} src={mbImage} />}
                title={
                  <>
                    {youthName}{' '}
                    <Tag
                      icon={
                        <MdEvent
                          style={{
                            fontSize: 13,
                            verticalAlign: 'text-top',
                          }}
                        />
                      }
                    >
                      {' '}
                      Completed: {dateCompleted}
                    </Tag>
                  </>
                }
                description={name}
              />
            </List.Item>
          );
        }}
      />
    </Modal>
  );
}
